<template>
    <div
        :class="[
            focused || valueLength ? 'is-focused' : '',
            styledInput ? 'styled-input' : '',
        ]"
    >
        <label v-if="label">{{ label }} {{ required ? '*' : '' }}</label>
        <input
            :value="value"
            :class="[
                ...classes,
                {
                    'is-invalid': externalError && externalMsgError,
                },
                { 'is-valid': value && externalValid },
            ]"
            :placeholder="placeholder"
            :required="required"
            :autocomplete="optionAutocomplete"
            :autocorrect="optionAutocorrect"
            :autocapitalize="optionAutocapitalize"
            :maxlength="maxLength"
            :disabled="disabled"
            :type="type"
            @blur="handleBlur"
            @input="handleInput"
            @keypress="numbersOnly"
            @focus="handleFocus"
        />
        <div v-if="externalMsgError" class="invalid-text">
            {{ externalMsgError }}
        </div>
    </div>
</template>

<script>
    const handleValidationTypes = {
        INPUT: 'input',
        BLUR: 'blur',
    };

    /* eslint-disable */
    const typeRules = {
        phone: {
            regExp: /^((8|\+7)[ ]?)?(\(?\d{3}\)?[ ]?)?[\d\- ]{7,10}$/,
            message: 'Некорректный формат номера',
        },
        email: {
            regExp: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
            message: 'Введите корректный e-mail',
        },
        ruName: {
            regExp: /^[а-яёa-zА-ЯЁA-Z\-]{2,}(\s+[а-яёa-zА-ЯЁA-Z\-]{1,})?(\s+[а-яёa-zА-ЯЁA-Z\-]{1,})?(\s+)?$/,
            message: 'Имя содержит недопустимые символы',
        },
        ruFullName: {
            regExp: /^[а-яёa-zА-ЯЁA-Z\-]{0,}(\s+)(\-)?(\s+)?[а-яёa-zА-ЯЁA-Z\-]{1,}(\s+[а-яёa-zА-ЯЁA-Z\-]{1,})?(\s+)?(\-)?(\s+)?(\s+[а-яёa-zА-ЯЁA-Z\-]{1,})?(\s+)?$/,
            message: 'ФИО содержит недопустимые символы',
        },
    };

    export default {
        props: {
            disabled: {
                default: false,
                type: Boolean,
            },
            label: {
                required: false,
                type: String,
            },
            placeholder: {
                required: false,
                type: String,
            },
            classes: {
                type: Array,
                default: () => {
                    return ['form-control'];
                },
            },
            externalValid: {
                default: false,
                type: Boolean,
            },
            externalError: {
                default: false,
                type: Boolean,
            },
            externalMsgError: {
                default: '',
                type: String,
            },
            value: {
                default: '',
                type: String,
            },
            type: {
                default: 'text',
                type: String,
            },
            required: {
                default: false,
                type: Boolean,
            },
            minLength: {
                type: Number,
            },
            maxLength: {
                type: Number,
                default: 1000,
            },
            validType: {
                type: String,
            },
            optionAutocomplete: {
                default: 'off',
                type: String,
            },
            optionAutocorrect: {
                default: 'off',
                type: String,
            },
            optionAutocapitalize: {
                default: 'off',
                type: String,
            },
            handleValidationType: {
                default: handleValidationTypes.INPUT,
                type: String,
            },
            trim: {
                default: false,
                type: Boolean,
            },
            styledInput: {
                default: false,
                type: Boolean,
            },
        },
        data() {
            return {
                error: false,
                errorMsg: '',
                focused: false,
            };
        },
        computed: {
            valueLength() {
                return this.value ? this.value.length : 0;
            },
        },
        mounted() {
            if (this.value) {
                this.validateInput(this.value);
            }
        },
        methods: {
            numbersOnly(evt) {
                if (this.type !== 'number') {
                    return false;
                }

                evt = evt ? evt : window.event;
                var charCode = evt.which ? evt.which : evt.keyCode;
                if (
                    charCode > 31 &&
                    (charCode < 48 || charCode > 57) &&
                    charCode !== 46
                ) {
                    evt.preventDefault();
                } else {
                    return true;
                }
            },
            handleInput(e) {
                if (this.handleValidationType === handleValidationTypes.INPUT) {
                    this.validateInput(
                        this.trim ? e.target.value.trim() : e.target.value
                    );
                }

                this.$emit('handleInput', e.target.value);
            },
            handleBlur(e) {
                if (this.trim) {
                    e.target.value = e.target.value.trim();
                    this.$emit('handleInput', e.target.value);
                }

                if (this.handleValidationType === handleValidationTypes.BLUR) {
                    this.validateInput(e.target.value);
                }

                this.$emit('handleBlur', e.target.value);
                this.focused = false;
            },
            handleFocus(e) {
                this.focused = true;
            },
            validateInput(value) {
                const valueLength = value ? value.length : 0;
                this.error = false;
                this.errorMsg = '';

                if (this.required && valueLength === 0) {
                    this.error = true;
                    this.errorMsg = `Поле обязательно для заполнения`;
                }

                if (
                    !this.error &&
                    this.minLength &&
                    this.minLength > valueLength
                ) {
                    this.error = true;
                    this.errorMsg = `Длина поля от ${this.minLength} символов. Сейчас ${valueLength}.`;
                }

                if (
                    !this.error &&
                    this.maxLength &&
                    this.maxLength < valueLength
                ) {
                    this.error = true;
                    this.errorMsg = `Длина поля до ${this.maxLength} символов. Сейчас ${valueLength}.`;
                }

                if (
                    value &&
                    !this.error &&
                    this.validType &&
                    typeRules[this.validType]
                ) {
                    const isValid =
                        typeRules[this.validType].regExp.test(value);

                    if (isValid) {
                        this.error = false;
                        this.errorMsg = '';
                    } else {
                        this.error = true;
                        this.errorMsg = typeRules[this.validType].message;
                    }
                }

                this.$emit('handleValidation', {
                    value,
                    isValid: !this.error,
                    message: this.errorMsg,
                });
            },
        },
    };
</script>

<style lang="less">
    .styled-input {
        position: relative;

        label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 95%;
            color: #8c8c8c;
            font-size: 15px;
            pointer-events: none;
            position: absolute;
            z-index: 10;
            cursor: text;
            left: 12px;
            top: 13px;
            transition: all 0.15s ease-in-out;
            margin-bottom: 0;
            line-height: 1.4;
        }

        .form-control {
            color: #333;
            font-size: 14px;
            height: 48px;
            line-height: 22px;
            outline: none;
            padding: 31px 12px 11px;
            position: relative;
            transition: 0.2s;
            width: 100%;
        }

        &.is-focused {
            label {
                top: 5px;
                font-size: 12px;
            }
        }
    }

    @media screen and (max-width: 379px) {
        .styled-input {
            label {
                font-size: 14px;
                top: 11px;
            }

            .form-control {
                height: 40px;
                padding: 20px 12px 6px;
            }
        }
    }
</style>
